interface UserInfo {
  email: string;
  name?: string;
  auth_provider: 'microsoft' | 'firebase' | 'demo' | 'admin' | 'client_view';
  user_id: string;
  is_admin: boolean;
  is_client_view?: boolean;
}

interface AuthResponse {
  message: string;
  token: string;
  user: {
    email: string;
    regions: string[];
  };
  regions: string[];
  auth_provider: string;
  user_id: string;
  is_admin: boolean;
  is_client_view?: boolean;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface FirebaseCredentials {
  firebase_token: string;
}

interface MicrosoftCredentials {
  access_token: string;
}

interface ClientViewCredentials {
  client_email: string;
}

type AuthCredentials = 
  | { auth_type: 'email_password'; email: string; password: string }
  | { auth_type: 'firebase'; firebase_token: string }
  | { auth_type: 'microsoft'; access_token: string }
  | { auth_type: 'client_view'; client_email: string };

class AuthService {
  private tokenKey = 'auth_token';
  private userKey = 'user_info';

  /**
   * Login with any authentication method
   */
  async login(credentials: AuthCredentials): Promise<UserInfo> {
    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      if (!response.ok) {
        let errorMessage = `Authentication failed: ${response.status}`;
        try {
          const errorData = await response.json();
          // Use the detailed error message from backend
          errorMessage = errorData.detail || errorData.message || errorMessage;
          
          // Special handling for authorization errors (403)
          if (response.status === 403) {
            errorMessage = errorData.detail || 'User not authorized. Please contact administrator to request access.';
          }
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data: AuthResponse = await response.json();
      
      // Store token and user info
      this.setToken(data.token);
      this.setUserInfo({
        email: data.user.email,
        auth_provider: data.auth_provider as 'microsoft' | 'firebase' | 'demo' | 'admin' | 'client_view',
        user_id: data.user_id,
        is_admin: data.is_admin,
        is_client_view: data.is_client_view
      });

      return {
        email: data.user.email,
        auth_provider: data.auth_provider as 'microsoft' | 'firebase' | 'demo' | 'admin' | 'client_view',
        user_id: data.user_id,
        is_admin: data.is_admin,
        is_client_view: data.is_client_view
      };
    } catch (error: any) {
      console.error('Authentication error:', error);
      
      // Handle network/CORS errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server. Please check if the backend is running and CORS is configured correctly.');
      }
      
      // Re-throw with original message if it's already an Error
      if (error instanceof Error) {
        throw error;
      }
      
      // Fallback for unknown errors
      throw new Error(`Authentication failed: ${error?.message || 'Unknown error'}`);
    }
  }

  /**
   * Email/password login (admin or demo)
   */
  async loginWithEmailPassword(credentials: LoginCredentials): Promise<UserInfo> {
    return this.login({
      auth_type: 'email_password',
      email: credentials.email,
      password: credentials.password
    });
  }

  /**
   * Firebase/Google login
   */
  async loginWithFirebase(credentials: FirebaseCredentials): Promise<UserInfo> {
    return this.login({
      auth_type: 'firebase',
      firebase_token: credentials.firebase_token
    });
  }

  /**
   * Microsoft login
   */
  async loginWithMicrosoft(credentials: MicrosoftCredentials): Promise<UserInfo> {
    return this.login({
      auth_type: 'microsoft',
      access_token: credentials.access_token
    });
  }

  /**
   * Client view login (admin viewing as specific client)
   */
  async loginAsClient(credentials: ClientViewCredentials): Promise<UserInfo> {
    return this.login({
      auth_type: 'client_view',
      client_email: credentials.client_email
    });
  }

  /**
   * Verify current token and get user info
   */
  async verifyToken(): Promise<UserInfo> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No token found');
    }

    try {
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';
      const response = await fetch(`${API_BASE_URL}/protected`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Token verification failed: ${response.status}`);
      }

      const data = await response.json();
      
      const userInfo = {
        email: data.user.email,
        auth_provider: data.auth_provider as 'microsoft' | 'firebase' | 'demo' | 'admin' | 'client_view',
        user_id: data.user_id,
        is_admin: data.is_admin,
        is_client_view: data.is_client_view
      };

      // Update stored user info
      this.setUserInfo(userInfo);
      
      return userInfo;
    } catch (error) {
      console.error('Token verification error:', error);
      // Clear invalid token
      this.logout();
      throw error;
    }
  }

  /**
   * Get current user info from storage
   */
  getCurrentUser(): UserInfo | null {
    return this.getUserInfo();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Logout user
   */
  logout(): void {
    // Get user info before clearing to check auth provider
    const userInfo = this.getUserInfo();
    
    // Remove token and user info from storage
    this.removeToken();
    this.removeUserInfo();
    
    // Clear MSAL sessionStorage if user was using Microsoft
    if (userInfo?.auth_provider === 'microsoft') {
      try {
        // Clear MSAL cache from sessionStorage
        const msalKeys = Object.keys(sessionStorage).filter(key => 
          key.startsWith('msal.') || key.includes('msal')
        );
        msalKeys.forEach(key => sessionStorage.removeItem(key));
      } catch (error) {
        console.error('Error clearing MSAL cache:', error);
      }
    }
    
    // Clear Firebase auth if user was using Firebase/Google
    if (userInfo?.auth_provider === 'firebase') {
      // Firebase auth state is managed separately, 
      // but we've already cleared the token which is what matters
    }
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Set token in storage
   */
  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  /**
   * Remove token from storage
   */
  private removeToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  /**
   * Get stored user info
   */
  private getUserInfo(): UserInfo | null {
    const userInfo = localStorage.getItem(this.userKey);
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * Set user info in storage
   */
  private setUserInfo(userInfo: UserInfo): void {
    localStorage.setItem(this.userKey, JSON.stringify(userInfo));
  }

  /**
   * Remove user info from storage
   */
  private removeUserInfo(): void {
    localStorage.removeItem(this.userKey);
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  validatePassword(password: string): boolean {
    return password.length >= 6;
  }
}

// Create and export a single instance
export const authService = new AuthService();

// Export types for use in other files
export type { UserInfo, AuthResponse, LoginCredentials, FirebaseCredentials, MicrosoftCredentials, ClientViewCredentials, AuthCredentials };
